import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Room, RoomEvent, Track, LocalAudioTrack, LocalVideoTrack, DisconnectReason } from 'livekit-client';
import {
  Mic, MicOff, Video, VideoOff, FlipHorizontal, X, Loader2, AlertTriangle, ShieldAlert,
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../api/client';
import liveDiag from '../lib/liveDiag'; // [LIVE-DIAG] temporary
import { useAuth } from '../context/AuthContext';
import LiveChatOverlay from './LiveChatOverlay';
import useLiveKitChat from '../hooks/useLiveKitChat';
import useRemoteGuestTracks from '../hooks/useRemoteGuestTracks';
import GuestVideoTile from './GuestVideoTile';

/**
 * LiveKitBroadcastRoom — publishes the host's camera+mic to a LiveKit room.
 *
 * Phase 24.E.3 — robustness rewrite:
 *
 *  ✅ ONE Room per `live.id` at any time, kept in a module-level cache so
 *     React 18 StrictMode (double-mount in dev) can't open a second Room
 *     and "Client initiated disconnect" the first.
 *  ✅ The cleanup function on unmount does NOT disconnect the Room — it only
 *     detaches the video element so the next mount can re-attach. The Room
 *     and its tracks survive route navigation. Disconnect happens only when
 *     the host clicks "Termina diretta" (or the page is actually closed).
 *  ✅ Mic/Cam toggles operate directly on the wrapped LocalAudioTrack /
 *     LocalVideoTrack via mute/unmute — no setMicrophoneEnabled which would
 *     try to re-acquire the device and lose our user-gesture context.
 *  ✅ DisconnectReason.CLIENT_INITIATED is treated as expected (no error UI).
 *  ✅ Mobile controls are in a horizontally-scrollable bar so they never get
 *     hidden under other blocks on narrow viewports.
 *  ✅ Phase-by-phase console.info('[LK][N/7]…') logging is preserved.
 *
 * Lifecycle outline:
 *   mount → look up sessionCache[live.id]
 *     ├─ found & connected → re-attach video preview, set phase='live'
 *     └─ not found        → preflight → getUserMedia → wrap tracks
 *                          → Room.connect → publish audio → publish video
 *                          → attach preview → cache session → phase='live'
 *   user clicks "Termina diretta" → disconnect room, stop tracks, drop cache,
 *                                   call onEnded()
 *   unmount (StrictMode or navigation) → ONLY detach video element. Tracks
 *                                        and Room stay alive in cache.
 */

/**
 * Module-level cache: liveId → { room, audioTrack, videoTrack, mediaStream }.
 * Single source of truth across StrictMode double-mount and route remounts.
 */
const sessionCache = new Map();

const endInFlight = new Map();

/** Phase 83 — page-level mic control. LiveDetailPage renders its own
 *  mute button next to "Termina live"; these module helpers operate on the
 *  cached session's audio track and keep every subscriber (in-video overlay
 *  + page button) in sync. */
const micListeners = new Map();

function notifyMicState(liveId, on) {
  const subs = micListeners.get(liveId);
  if (subs) subs.forEach((fn) => { try { fn(on); } catch (_e) { /* noop */ } });
}

/** true = mic live, false = muted, null = no published audio track yet. */
export function getMicSessionState(liveId) {
  const t = sessionCache.get(liveId)?.audioTrack;
  return t ? !t.isMuted : null;
}

export function subscribeMicSession(liveId, fn) {
  if (!micListeners.has(liveId)) micListeners.set(liveId, new Set());
  micListeners.get(liveId).add(fn);
  return () => { micListeners.get(liveId)?.delete(fn); };
}

/** Mutes/unmutes the PUBLISHED LiveKit audio track (viewers hear silence
 *  server-side). Returns the new "mic on" state, or null if no track. */
export async function toggleMicSession(liveId) {
  const t = sessionCache.get(liveId)?.audioTrack;
  if (!t) return null;
  try {
    if (t.isMuted) await t.unmute(); else await t.mute();
    const on = !t.isMuted;
    notifyMicState(liveId, on);
    return on;
  } catch (e) {
    console.warn('[LK] toggleMicSession failed', e);
    return null;
  }
}

/** Full local cleanup: stop tracks, remove listeners, disconnect, drop cache. */
export async function teardownBroadcastSession(liveId) {
  const cached = sessionCache.get(liveId);
  if (!cached) return;
  sessionCache.delete(liveId);
  if (cached.heartbeat) { try { clearInterval(cached.heartbeat); } catch (_e) { /* noop */ } }
  try { cached.room?.removeAllListeners(); } catch (_e) { /* noop */ }
  try { await cached.room?.disconnect(); } catch (_e) { /* noop */ }
  try { cached.audioTrack?.stop(); } catch (_e) { /* noop */ }
  try { cached.videoTrack?.stop(); } catch (_e) { /* noop */ }
  try { cached.mediaStream?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
  liveDiag('streams_cleanup_done', { live_id: liveId }); // [LIVE-DIAG]
}

/**
 * Centralized "Termina Live" — single-flight per live. Awaits the backend
 * confirmation FIRST, then tears down camera/mic/room locally. Both the
 * in-video X button and the page-level "End live" button go through here.
 * If the backend call fails the local session is kept so the creator can
 * retry (nothing half-closed).
 */
export function endLiveSession(liveId) {
  if (endInFlight.has(liveId)) return endInFlight.get(liveId);
  const p = (async () => {
    try {
      liveDiag('live_end_requested', { live_id: liveId }); // [LIVE-DIAG]
      await api.post(`/lives/${liveId}/end`);
      liveDiag('live_end_confirmed', { live_id: liveId }); // [LIVE-DIAG]
      await teardownBroadcastSession(liveId);
    } finally {
      endInFlight.delete(liveId);
    }
  })();
  endInFlight.set(liveId, p);
  return p;
}

/**
 * Phase B34.1 — Build a MediaTrackConstraints video object that matches the
 * live's chosen aspect ratio (16:9 / 1:1 / 9:16). Without these constraints
 * the browser defaults to 4:3 or native sensor aspect, which after a camera
 * flip caused the published track to "fall back" to a different ratio,
 * which the viewer rendered as a cropped / letterboxed feed (the user
 * reported this as «il formato si resetta dopo cambio camera»).
 *
 * Phase B37.J — for non-16:9 formats we use `aspectRatio: { ideal }` +
 * `resizeMode: 'crop-and-scale'` (Chrome-only hint) so the browser
 * hardware-crops the sensor output to the exact ratio we want. Without
 * this the desktop webcam publishes its native 16:9 and both the
 * broadcaster monitor and the viewer see a letterboxed feed. On mobile
 * browsers that ignore `resizeMode`, the standard aspectRatio hint is
 * enough because most phones expose portrait-native tracks anyway.
 */
const videoConstraintsForFormat = (fmt, facingHint) => {
  const f = fmt || '16:9';
  let width, height;
  if (f === '9:16') { width = 720;  height = 1280; }
  else if (f === '1:1') { width = 720; height = 720; }
  else                  { width = 1280; height = 720; }
  const out = {
    width:  { ideal: width },
    height: { ideal: height },
    aspectRatio: { ideal: width / height },
  };
  // For non-landscape ratios, hint the browser to crop-and-scale to the
  // target ratio instead of letterboxing. Chrome/Edge honor this; Safari
  // and Firefox ignore it silently — which is fine, they fall back to
  // the aspectRatio hint above.
  if (f !== '16:9') {
    out.resizeMode = 'crop-and-scale';
  }
  if (facingHint) out.facingMode = { ideal: facingHint };
  return out;
};

const LiveKitBroadcastRoom = ({ live, onEnded, embedded = false, onRoom = null, onChat = null }) => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation('lives');
  const audioOnly = live?.kind === 'audio';
  const videoRef = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [activeRoom, setActiveRoom] = useState(null);
  const [status, setStatus] = useState('idle');     // idle | running | error | denied
  const [errorTitle, setErrorTitle] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(!audioOnly);
  const [facing, setFacing] = useState('user');
  // Phase 26.B — explicit list of available cameras + current index, so
  // "switch camera" cycles through ALL detected devices (front, back,
  // ultrawide, external webcam, …) instead of relying on facingMode
  // which is unreliable on Android Chrome with multi-camera devices.
  const [videoDevices, setVideoDevices] = useState([]);
  const [videoDeviceIdx, setVideoDeviceIdx] = useState(0);
  // Synchronous guard against StrictMode double-effect for the CURRENT mount.
  const startedRef = useRef(false);
  // Phase 26.G — interval id for the broadcaster heartbeat ping
  const heartbeatTimerRef = useRef(null);

  // Phase B37.H — auto-hide dei controlli come Instagram/FaceTime.
  // I controlli restano visibili 3s dopo l'ultimo tap; poi svaniscono.
  // Tap sullo schermo → riappaiono.
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsHideTimerRef = useRef(null);
  const scheduleHideControls = React.useCallback(() => {
    if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);
  const showControls = React.useCallback(() => {
    setControlsVisible(true);
    scheduleHideControls();
  }, [scheduleHideControls]);
  useEffect(() => {
    if (phase === 'live') scheduleHideControls();
    return () => {
      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    };
  }, [phase, scheduleHideControls]);

  const describeError = (err, stage) => {
    const name = err?.name || '';
    const msg = err?.message || String(err || '');
    console.error(`[LK][stage=${stage}] ${name}: ${msg}`, err);
    let title = t('broadcast.error_title_default');
    let detail = msg;
    let hint = '';
    switch (name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        title = t('broadcast.error_title_denied');
        detail = audioOnly
          ? t('broadcast.error_detail_denied_audio')
          : t('broadcast.error_detail_denied_full');
        hint = t('broadcast.error_hint_denied');
        setStatus('denied'); break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        title = t('broadcast.error_title_no_devices');
        detail = t('broadcast.error_detail_no_devices');
        hint = t('broadcast.error_hint_no_devices');
        setStatus('error'); break;
      case 'NotReadableError':
      case 'TrackStartError':
        title = t('broadcast.error_title_in_use');
        detail = t('broadcast.error_detail_in_use');
        hint = t('broadcast.error_hint_in_use');
        setStatus('error'); break;
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        title = t('broadcast.error_title_constraint');
        detail = t('broadcast.error_detail_constraint', { constraint: err?.constraint || '' });
        hint = t('broadcast.error_hint_constraint');
        setStatus('error'); break;
      case 'SecurityError':
        title = t('broadcast.error_title_security');
        detail = t('broadcast.error_detail_security');
        hint = t('broadcast.error_hint_security');
        setStatus('error'); break;
      case 'AbortError':
        title = t('broadcast.error_title_abort');
        detail = t('broadcast.error_detail_abort');
        hint = t('broadcast.error_hint_abort');
        setStatus('error'); break;
      case 'TypeError':
        title = t('broadcast.error_title_api');
        detail = t('broadcast.error_detail_api');
        hint = t('broadcast.error_hint_api');
        setStatus('error'); break;
      default:
        if (/connect|websocket|network|timeout|ECONNREFUSED|ENOTFOUND/i.test(msg)) {
          title = t('broadcast.error_title_connection');
          detail = msg;
          hint = t('broadcast.error_hint_connection');
        }
        setStatus('error'); break;
    }
    setErrorTitle(title); setErrorDetail(detail); setErrorHint(hint);
  };

  const preflight = () => {
    setPhase('preflight');
    console.info('[LK][1/7] preflight start', {
      isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : 'n/a',
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'n/a',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
    });
    if (typeof window === 'undefined' || !window.isSecureContext) {
      describeError({ name: 'SecurityError', message: 'isSecureContext=false' }, 'preflight'); return false;
    }
    if (!navigator?.mediaDevices) {
      describeError({ name: 'TypeError', message: 'navigator.mediaDevices is undefined' }, 'preflight'); return false;
    }
    if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
      describeError({ name: 'TypeError', message: 'getUserMedia is not a function' }, 'preflight'); return false;
    }
    if (!live?.livekit_url || !live?.livekit_token) {
      describeError({ name: 'Error', message: t('broadcast.token_unavailable') }, 'preflight');
      return false;
    }
    return true;
  };

  const startBroadcast = async () => {
    if (startedRef.current) {
      console.info('[LK] startBroadcast: already running on this mount, ignored');
      return;
    }
    startedRef.current = true;
    setStatus('running');
    setErrorTitle(''); setErrorDetail(''); setErrorHint('');

    // ============ FAST PATH — re-use cached session ============
    const cached = sessionCache.get(live.id);
    if (cached && cached.room?.state === 'connected') {
      console.info('[LK] reusing cached session for live', live.id, '(StrictMode/remount)');
      try {
        if (!audioOnly && cached.videoTrack && videoRef.current) {
          cached.videoTrack.attach(videoRef.current);
        }
      } catch (e) { console.warn('[LK] re-attach failed', e); }
      // Sync UI state with the live mute state of the cached tracks.
      const cachedMicOn = !!cached.audioTrack && !cached.audioTrack.isMuted;
      setMicOn(cachedMicOn);
      notifyMicState(live.id, cachedMicOn);
      setCamOn(!!cached.videoTrack && !cached.videoTrack.isMuted);
      setActiveRoom(cached.room);
      if (typeof onRoom === 'function') onRoom(cached.room);
      setPhase('live');
      return;
    }

    if (!preflight()) return;

    // ============ PHASE 2 — getUserMedia ============
    setPhase('permissions');
    console.info('[LK][2/7] requesting getUserMedia', { audioOnly });
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: audioOnly ? false : videoConstraintsForFormat(live?.video_format, 'user'),
      });
      console.info('[LK][2/7] getUserMedia OK', {
        a: stream.getAudioTracks().map(t => t.label),
        v: stream.getVideoTracks().map(t => t.label),
      });
    } catch (e) { describeError(e, 'getUserMedia'); return; }

    // Phase 26.B — enumerate available video inputs now that the user
    // has granted permission (labels are empty until permission is given).
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput' && d.deviceId);
      setVideoDevices(cams);
      // Find the index of the currently-active camera so flipCam can
      // start cycling from the right position.
      const activeTrack = stream.getVideoTracks()[0];
      const activeId = activeTrack?.getSettings?.()?.deviceId;
      const idx = cams.findIndex(c => c.deviceId === activeId);
      setVideoDeviceIdx(idx >= 0 ? idx : 0);
      console.info('[LK] available cameras', { count: cams.length, activeIdx: idx });
    } catch (e) { console.warn('[LK] enumerateDevices failed', e); }

    // ============ PHASE 3 — wrap tracks ============
    setPhase('tracks');
    let audioTrack = null;
    let videoTrack = null;
    try {
      const aRaw = stream.getAudioTracks()[0];
      if (aRaw) audioTrack = new LocalAudioTrack(aRaw);
      if (!audioOnly) {
        const vRaw = stream.getVideoTracks()[0];
        if (vRaw) videoTrack = new LocalVideoTrack(vRaw);
      }
      console.info('[LK][3/7] wrapped tracks', { audio: !!audioTrack, video: !!videoTrack });
    } catch (e) { describeError(e, 'wrap-tracks'); return; }

    // ============ PHASE 4 — connect ============
    setPhase('connecting');
    console.info('[LK][4/7] connecting to', live.livekit_url);
    const room = new Room({ adaptiveStream: true, dynacast: true });
    // [LIVE-DIAG] temporary — reconnection visibility
    room.on(RoomEvent.Reconnecting, () => liveDiag('lk_reconnecting', { live_id: live.id, role: 'host' }));
    room.on(RoomEvent.Reconnected, () => liveDiag('lk_reconnected', { live_id: live.id, role: 'host' }));
    room.on(RoomEvent.Disconnected, (reason) => {
      console.warn('[LK] room disconnected', reason);
      // CLIENT_INITIATED means "Termina diretta" was hit on purpose.
      if (reason === DisconnectReason.CLIENT_INITIATED) return;
      // Anything else (network drop, kicked, server shutdown) should surface.
      describeError({ name: 'NetworkError', message: t('broadcast.error_detail_disconnected', { reason: reason || t('broadcast.reason_unknown') }) }, 'disconnected');
    });
    try {
      await room.connect(live.livekit_url, live.livekit_token);
      console.info('[LK][4/7] connected', { sid: room.localParticipant?.sid });
    } catch (e) {
      describeError(e, 'connect');
      try { stream.getTracks().forEach(t => t.stop()); } catch (_e) { /* noop */ }
      return;
    }

    // ============ PHASE 5/6 — publish ============
    setPhase('publishing');
    try {
      if (audioTrack) { console.info('[LK][5/7] publishing audio'); await room.localParticipant.publishTrack(audioTrack, { source: Track.Source.Microphone }); }
    } catch (e) { describeError(e, 'publish-audio'); try { room.disconnect(); } catch (_e) {/* noop */} return; }
    try {
      if (videoTrack) { console.info('[LK][6/7] publishing video'); await room.localParticipant.publishTrack(videoTrack, { source: Track.Source.Camera }); }
    } catch (e) { describeError(e, 'publish-video'); try { room.disconnect(); } catch (_e) {/* noop */} return; }

    // ============ PHASE 7 — attach preview + persist session ============
    try {
      if (!audioOnly && videoTrack && videoRef.current) {
        videoTrack.attach(videoRef.current);
        console.info('[LK][7/7] preview attached');
      }
    } catch (e) { console.warn('[LK][7/7] preview attach failed', e); }

    liveDiag('host_tracks_published', { live_id: live.id, audio: !!audioTrack, video: !!videoTrack }); // [LIVE-DIAG]
    sessionCache.set(live.id, { room, audioTrack, videoTrack, mediaStream: stream });
    setActiveRoom(room);
    if (typeof onRoom === 'function') onRoom(room);
    setPhase('live');
    setMicOn(true);
    notifyMicState(live.id, true);
    setCamOn(!audioOnly);
    // Phase 26.G — start the broadcaster heartbeat. The server stamps
    // `last_heartbeat_at` on each ping. Feeds require a heartbeat <120s
    // and the admin close-stale endpoint closes lives missing >180s of
    // heartbeats. 30s cadence gives 3-4 missed beats of grace.
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    const sendBeat = async () => {
      try {
        await api.post(`/lives/${live.id}/heartbeat`);
      } catch (e) { console.warn('[LK] heartbeat failed', e?.message || e); }
    };
    sendBeat(); // immediate first beat
    heartbeatTimerRef.current = setInterval(sendBeat, 30000);
    // Store the interval on the cache entry so teardownBroadcastSession can
    // clear it from any code path (page-level End button included).
    const entry = sessionCache.get(live.id);
    if (entry) entry.heartbeat = heartbeatTimerRef.current;
    toast({ title: t('broadcast.live_started_title'), description: t('broadcast.live_started_desc') });
  };

  useEffect(() => {
    startBroadcast();
    // IMPORTANT: do NOT disconnect on unmount. StrictMode re-mount + remounting
    // when the parent re-fetches `live` would tear down the Room and produce
    // a "Client initiated disconnect" banner. Disconnection only happens
    // explicitly via the "Termina diretta" button (see `end()` below).
    return () => {
      // Detach the local video element so it doesn't hold a dangling reference.
      try {
        const cached = sessionCache.get(live?.id);
        if (cached?.videoTrack && videoRef.current) {
          cached.videoTrack.detach(videoRef.current);
        }
      } catch (_e) { /* noop */ }
      // Reset only the per-mount lock; the cached Room/tracks survive.
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.id]);

  /** Toggle the published audio track via mute()/unmute(). This survives
   *  multiple toggles because we operate directly on our wrapped track. */
  const toggleMic = async () => {
    const cached = sessionCache.get(live.id);
    const t = cached?.audioTrack;
    if (!t) return;
    try {
      if (t.isMuted) await t.unmute(); else await t.mute();
      setMicOn(!t.isMuted);
      notifyMicState(live.id, !t.isMuted);
      console.info('[LK] mic isMuted=', t.isMuted);
    } catch (e) { console.warn('[LK] toggleMic failed', e); }
  };

  // Phase 83 — stay in sync when the page-level mic button toggles the track.
  useEffect(() => {
    if (!live?.id) return undefined;
    return subscribeMicSession(live.id, setMicOn);
  }, [live?.id]);

  const toggleCam = async () => {
    if (audioOnly) return;
    const cached = sessionCache.get(live.id);
    const t = cached?.videoTrack;
    if (!t) return;
    try {
      if (t.isMuted) await t.unmute(); else await t.mute();
      setCamOn(!t.isMuted);
      console.info('[LK] cam isMuted=', t.isMuted);
    } catch (e) { console.warn('[LK] toggleCam failed', e); }
  };

  const flipBusyRef = useRef(false);
  const flipCam = async () => {
    // Concurrency guard — a double-tap mid-switch corrupts the track state.
    if (flipBusyRef.current) return;
    flipBusyRef.current = true;
    try {
      await doFlipCam();
    } finally {
      flipBusyRef.current = false;
    }
  };

  const doFlipCam = async () => {
    if (audioOnly) return;
    const cached = sessionCache.get(live.id);
    const room = cached?.room;
    if (!room) return;
    // NeoUI fix (Jun 2026) — tracks used to be published WITHOUT an explicit
    // `source`, so LiveKit registered them as Unknown and
    // getTrackPublication(Track.Source.Camera) returned undefined → flipCam
    // bailed out silently on EVERY platform. We now publish with the correct
    // source AND fall back to the cached wrapped track (source of truth).
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const camTrack = cached?.videoTrack || pub?.track;
    if (!camTrack) {
      console.warn('[LK] flipCam: no camera track available, cannot switch');
      return;
    }

    // NeoUI fix — STRICT front↔back toggle. The old logic cycled through
    // ALL video inputs by index: on multi-lens phones (wide/ultrawide/tele)
    // it stepped through 3-4 rear lenses before reaching the front cam →
    // «il pulsante non fa niente». Additionally `facingMode:{ideal}` never
    // FORCES the swap (the browser may return the same camera).
    const next = facing === 'user' ? 'environment' : 'user';

    // Pick an explicit device whose label matches the target facing.
    const rearRe = /back|rear|environment|posteriore|trasera/i;
    const frontRe = /front|user|face|anteriore|frontale/i;
    const wantRe = next === 'environment' ? rearRe : frontRe;
    const activeId = camTrack.mediaStreamTrack?.getSettings?.()?.deviceId;
    let target = videoDevices.find(d => d.deviceId !== activeId && wantRe.test(d.label || '')) || null;
    // Unlabeled devices but exactly two cameras → the other one is the flip.
    if (!target && videoDevices.length === 2) {
      target = videoDevices.find(d => d.deviceId !== activeId) || null;
    }
    console.info('[LK] flipCam →', { next, targetLabel: target?.label, targetId: target?.deviceId, devices: videoDevices.length });
    liveDiag('camera_flip_tap', { live_id: live.id, to: next, devices: videoDevices.length }); // [LIVE-DIAG]

    const finalize = async (how) => {
      if (videoRef.current) {
        try { camTrack.detach(videoRef.current); } catch (_e) { /* noop */ }
        camTrack.attach(videoRef.current);
      }
      setFacing(next);
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter(d => d.kind === 'videoinput' && d.deviceId);
        setVideoDevices(cams);
        const newId = camTrack.mediaStreamTrack?.getSettings?.()?.deviceId;
        const idx = cams.findIndex(c => c.deviceId === newId);
        if (idx >= 0) setVideoDeviceIdx(idx);
      } catch (_e) { /* noop */ }
      console.info(`[LK] flipCam OK via ${how}`);
      liveDiag('camera_flip_ok', { live_id: live.id, via: how, to: next }); // [LIVE-DIAG]
      try { toast({ title: t('broadcast.camera_switched_title'), description: next === 'environment' ? t('broadcast.camera_switched_back') : t('broadcast.camera_switched_front') }); } catch (_e) { /* noop */ }
    };

    // --- Strategy A: switchActiveDevice + restartTrack(deviceId.exact + format) ---
    if (target?.deviceId) {
      try {
        if (typeof room.switchActiveDevice === 'function') {
          await room.switchActiveDevice('videoinput', target.deviceId);
          // switchActiveDevice re-creates the MediaStreamTrack with default
          // constraints → restore the chosen video_format via restartTrack.
          try {
            await camTrack.restartTrack({
              deviceId: { exact: target.deviceId },
              ...videoConstraintsForFormat(live?.video_format),
            });
          } catch (_e) { /* best-effort: keep current track */ }
          await finalize('switchActiveDevice');
          return;
        }
      } catch (e) {
        console.warn('[LK] flipCam strategy A (switchActiveDevice) failed, trying B', e?.message || e);
      }

      // --- Strategy B: restartTrack with deviceId.exact + format ---
      try {
        await camTrack.restartTrack({
          deviceId: { exact: target.deviceId },
          ...videoConstraintsForFormat(live?.video_format),
        });
        await finalize('restartTrack(deviceId.exact)');
        return;
      } catch (e) {
        console.warn('[LK] flipCam strategy B (restartTrack deviceId) failed, trying C', e?.message || e);
      }
    }

    // --- Strategy C: restartTrack with facingMode EXACT (forces the swap) ---
    // `exact` is mandatory here: with `ideal` iOS Safari / Android Chrome
    // may silently keep the current camera → the button appears broken.
    try {
      await camTrack.restartTrack({
        ...videoConstraintsForFormat(live?.video_format),
        facingMode: { exact: next },
      });
      await finalize('restartTrack(facingMode.exact)');
      return;
    } catch (e) {
      console.warn('[LK] flipCam strategy C (facingMode.exact) failed, trying D', e?.message || e);
    }

    // --- Strategy D: manual getUserMedia(facingMode.exact) + replaceTrack ---
    try {
      let newStream;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: { ...videoConstraintsForFormat(live?.video_format), facingMode: { exact: next } },
        });
      } catch (_e1) {
        // Some builds reject `exact` in gUM → retry with ideal + deviceId hint.
        const cfg = videoConstraintsForFormat(live?.video_format, next);
        if (target?.deviceId) cfg.deviceId = { ideal: target.deviceId };
        newStream = await navigator.mediaDevices.getUserMedia({ video: cfg });
      }
      const newMediaTrack = newStream.getVideoTracks()[0];
      if (!newMediaTrack) throw new Error('No video track from manual getUserMedia');
      const oldMediaTrack = camTrack.mediaStreamTrack;
      if (typeof camTrack.setMediaStreamTrack === 'function') {
        await camTrack.setMediaStreamTrack(newMediaTrack);
      } else {
        const sender = camTrack.sender || pub?.sender;
        if (sender && typeof sender.replaceTrack === 'function') {
          await sender.replaceTrack(newMediaTrack);
        } else {
          throw new Error('No replaceTrack path available');
        }
      }
      // Release the previous camera hardware.
      try { if (oldMediaTrack && oldMediaTrack !== newMediaTrack) oldMediaTrack.stop(); } catch (_e) { /* noop */ }
      await finalize('manual gUM + replaceTrack');
    } catch (e3) {
      console.error('[LK] flipCam ALL strategies failed', e3?.message || e3);
      liveDiag('camera_flip_failed', { live_id: live.id, err: e3?.name || String(e3?.message || 'unknown').slice(0, 60) }); // [LIVE-DIAG]
      try { toast({ title: t('broadcast.camera_switch_failed_title'), description: t('broadcast.camera_switch_failed_desc'), variant: 'destructive' }); } catch (_e) { /* noop */ }
    }
  };

  const [ending, setEnding] = useState(false);
  const end = async () => {
    // Phase 76 — centralized, single-flight, AWAITED end (endLiveSession).
    // The old fire-and-forget POST raced against the immediate refresh():
    // the page re-read status='live' before the server closed the room and
    // the button looked broken (reported mostly on iOS).
    if (ending) return;
    setEnding(true);
    try {
      await endLiveSession(live.id);
      heartbeatTimerRef.current = null; // interval already cleared by teardown
      onEnded?.();
    } catch (e) {
      console.error('[LK] end live failed', e?.message || e);
      try { toast({ title: t('broadcast.end_failed_title'), description: t('broadcast.end_failed_desc'), variant: 'destructive' }); } catch (_e) { /* noop */ }
    } finally {
      setEnding(false);
    }
  };

  const retry = async () => {
    // Drop any partial cached session and rerun from scratch.
    await teardownBroadcastSession(live.id);
    heartbeatTimerRef.current = null;
    startedRef.current = false;
    setStatus('idle'); setPhase('idle');
    setErrorTitle(''); setErrorDetail(''); setErrorHint('');
    setTimeout(() => { startBroadcast(); }, 0);
  };

  // ---------------- RENDER ----------------
  // Wrap class — when embedded in the parent's main aspect-ratio container we
  // fill it 100%; otherwise we render our own aspect-video block.
  const wrapClass = embedded
    ? 'absolute inset-0 flex flex-col'
    : 'space-y-3';
  const videoBoxClass = embedded
    ? 'relative h-full w-full bg-black'
    : 'relative aspect-video w-full overflow-hidden rounded-lg bg-black';

  // Phase 28 — wire LiveKit chat (creator = host). MUST be unconditional so
  // the hook order matches every render (rules-of-hooks).
  const chat = useLiveKitChat({
    room: activeRoom,
    currentUser,
    isCreator: true,
    hostUserId: currentUser?.id,
  });
  // Phase 53 · Live Now guest hotfix — the creator must SEE and HEAR
  // co-hosts. Before this, the broadcast room had NO remote-track
  // handling at all: an accepted guest published fine but was invisible
  // and inaudible for the host. Every remote participant with published
  // tracks (only co-hosts can publish) gets a tile + audio element.
  const remoteGuests = useRemoteGuestTracks(activeRoom, currentUser?.id);
  // Bubble the chat state up so a parent (e.g. LiveDashboardPage) can mount
  // the full sidebar version using the SAME message buffer.
  useEffect(() => {
    if (typeof onChat === 'function') onChat(chat);
  }, [chat, onChat]);

  if (status === 'denied') {
    return (
      <div className={`${embedded ? 'absolute inset-0' : ''} flex flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center`}
           data-testid="broadcast-room-denied">
        <ShieldAlert className="h-8 w-8 text-red-500" />
        <p className="text-sm font-semibold text-red-800">{errorTitle || t('broadcast.error_permissions_needed')}</p>
        {errorDetail && <p className="text-xs text-red-700" data-testid="broadcast-error-detail">{errorDetail}</p>}
        {errorHint && <p className="text-[11px] text-red-600/80" data-testid="broadcast-error-hint">{errorHint}</p>}
        <Button onClick={retry} variant="outline" data-testid="broadcast-room-retry">{t('broadcast.retry')}</Button>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={`${embedded ? 'absolute inset-0' : ''} flex flex-col items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-6 text-center`}
           data-testid="broadcast-room-error">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <p className="text-sm font-semibold">{errorTitle || t('broadcast.error_title_default')}</p>
        {errorDetail && <p className="text-xs text-amber-800" data-testid="broadcast-error-detail">{errorDetail}</p>}
        {errorHint && <p className="text-[11px] text-amber-700/80" data-testid="broadcast-error-hint">{errorHint}</p>}
        <p className="text-[10px] text-muted-foreground">{t('broadcast.phase_failed_prefix')}: <code>{phase}</code></p>
        <Button onClick={retry} variant="outline" data-testid="broadcast-room-retry">{t('broadcast.retry')}</Button>
      </div>
    );
  }

  const phaseLabel = {
    idle: t('broadcast.phase.idle'),
    preflight: t('broadcast.phase.preflight'),
    permissions: t('broadcast.phase.permissions'),
    tracks: t('broadcast.phase.tracks'),
    connecting: t('broadcast.phase.connecting'),
    publishing: t('broadcast.phase.publishing'),
    live: t('broadcast.phase.live'),
  }[phase] || t('broadcast.phase.idle');

  // Phase 28 — broadcast aspect ratio chosen by the creator at scheduling time.
  // Falls back to 16:9 for legacy lives. Embedded mode keeps full-bleed.
  const aspectClass = (() => {
    const fmt = live?.video_format || '16:9';
    if (embedded) return '';
    if (fmt === '9:16') return 'aspect-[9/16] max-w-md mx-auto';
    if (fmt === '1:1') return 'aspect-square max-w-2xl mx-auto';
    return 'aspect-video';
  })();
  const videoBoxClassWithAspect = embedded
    ? videoBoxClass
    : `relative w-full overflow-hidden rounded-lg bg-black ${aspectClass}`;

  return (
    <div className={wrapClass} data-testid="livekit-broadcast-room" onClick={showControls}>
      {!audioOnly ? (
        <div className={videoBoxClassWithAspect}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            // Phase B37.J · P0 · Vertical Lives fix
            // Per formati non-16:9 usiamo `object-cover` così il container
            // (già dimensionato dall'aspectClass del parent) viene riempito
            // ed il creator vede LA STESSA IMMAGINE 9:16 / 1:1 che vedranno
            // gli spettatori. Con `object-contain` il preview mostrava il
            // 16:9 nativo della webcam letterboxato dentro un box 9:16,
            // dando la sensazione errata di trasmettere in landscape.
            // Per 16:9 restiamo su `object-contain` (rispetta l'aspect
            // nativo di webcam desktop → nessun crop indesiderato).
            // La CSS `scaleX(-1)` specchia SOLO la preview locale della
            // camera frontale (come Instagram/FaceTime). Il track pubblicato
            // resta NON specchiato per gli spettatori (WebRTC ignora le
            // transform CSS del preview).
            className={`h-full w-full ${
              (live?.video_format === '9:16' || live?.video_format === '1:1')
                ? 'object-cover'
                : 'object-contain'
            }`}
            style={{
              transform: facing === 'user' ? 'scaleX(-1)' : 'none',
              transition: 'transform 0.2s ease',
            }}
            data-testid="livekit-preview-video"
          />
          {/* Phase 53 · Co-host tiles — the host sees & hears guests here. */}
          {remoteGuests.length > 0 && (
            <div
              className="absolute left-3 top-12 z-10 flex flex-col gap-2"
              data-testid="broadcast-guest-tiles"
            >
              {remoteGuests.map((g) => <GuestVideoTile key={g.identity} guest={g} />)}
            </div>
          )}
          {/* Phase 28 — overlay chat (Instagram-style, 5 msg, fade 8s) */}
          {phase === 'live' && (
            <LiveChatOverlay messages={chat.messages} visibleCount={5} fadeMs={8000} />
          )}
          {phase !== 'live' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white" data-testid="livekit-phase-overlay">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-xs font-medium">{phaseLabel}</span>
              <span className="text-[10px] text-white/60">{phase}</span>
            </div>
          )}
        </div>
      ) : (
        <div className={embedded ? 'absolute inset-0 flex items-center justify-center bg-card' : 'flex flex-col items-center justify-center gap-2 rounded-lg bg-card p-12'} data-testid="livekit-audio-room">
          <Mic className="h-12 w-12 gold-text" />
          {/* Phase 53 · audio-only lives: guest audio tiles for the host. */}
          {remoteGuests.length > 0 && (
            <div className="absolute left-3 top-3 flex flex-col gap-2" data-testid="broadcast-guest-tiles-audio">
              {remoteGuests.map((g) => <GuestVideoTile key={g.identity} guest={g} />)}
            </div>
          )}
          {phase === 'live'
            ? <span className="ml-3 font-semibold">{t('broadcast.audio_live_active')}</span>
            : <span className="ml-3 text-xs text-muted-foreground"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> {phaseLabel}</span>}
        </div>
      )}

      {/* Phase B37.H · P1 — Controlli circolari traslucidi, auto-hide 3s.
          Posizionati SOPRA la chat (top-right in embedded mode) così i
          messaggi restano sempre leggibili. Safe-area iPhone rispettata. */}
      <div
        className={
          embedded
            ? `pointer-events-none absolute right-0 top-0 z-20 flex flex-col items-end gap-2.5 p-3 sm:p-4 transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`
            : 'flex flex-wrap items-center gap-2'
        }
        style={
          embedded
            ? {
                paddingTop: 'max(env(safe-area-inset-top, 0px), 0.75rem)',
                paddingRight: 'max(env(safe-area-inset-right, 0px), 0.75rem)',
              }
            : undefined
        }
        data-testid="broadcast-controls"
        onClick={(e) => { e.stopPropagation(); showControls(); }}
      >
        <Button
          data-testid="broadcast-toggle-mic"
          onClick={toggleMic}
          variant={embedded ? 'ghost' : (micOn ? 'default' : 'destructive')}
          size={embedded ? 'icon' : 'sm'}
          className={
            embedded
              ? `${controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'} h-11 w-11 rounded-full bg-black/50 text-white backdrop-blur-md hover:bg-black/70 ${!micOn ? 'bg-red-500/80 hover:bg-red-500' : ''}`
              : 'shrink-0 gap-1'
          }
          disabled={phase !== 'live'}
          aria-pressed={!micOn}
          aria-label={micOn ? t('broadcast.mic_off_label') : t('broadcast.mic_on_label')}
        >
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          {!embedded && <span className="hidden sm:inline">{micOn ? t('broadcast.mic_on') : t('broadcast.mic_off')}</span>}
        </Button>
        {!audioOnly && (
          <>
            <Button
              data-testid="broadcast-toggle-cam"
              onClick={toggleCam}
              variant={embedded ? 'ghost' : (camOn ? 'default' : 'destructive')}
              size={embedded ? 'icon' : 'sm'}
              className={
                embedded
                  ? `${controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'} h-11 w-11 rounded-full bg-black/50 text-white backdrop-blur-md hover:bg-black/70 ${!camOn ? 'bg-red-500/80 hover:bg-red-500' : ''}`
                  : 'shrink-0 gap-1'
              }
              disabled={phase !== 'live'}
              aria-pressed={!camOn}
              aria-label={camOn ? t('broadcast.cam_off_label') : t('broadcast.cam_on_label')}
            >
              {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
              {!embedded && <span className="hidden sm:inline">{camOn ? t('broadcast.cam_on') : t('broadcast.cam_off')}</span>}
            </Button>
            <Button
              data-testid="broadcast-swap-cam"
              onClick={flipCam}
              variant={embedded ? 'ghost' : 'outline'}
              size={embedded ? 'icon' : 'sm'}
              className={
                embedded
                  ? `${controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'} h-11 w-11 rounded-full bg-black/50 text-white backdrop-blur-md hover:bg-black/70 disabled:opacity-40`
                  : 'shrink-0 gap-1'
              }
              disabled={phase !== 'live'}
              title={videoDevices.length <= 1 ? t('broadcast.flip_camera_one_only') : t('broadcast.flip_camera_available', { count: videoDevices.length })}
              aria-label={t('broadcast.flip_cam_label')}
            >
              <FlipHorizontal className="h-5 w-5" />
              {!embedded && (
                <span className="hidden sm:inline">
                  {videoDevices.length > 1 ? t('broadcast.flip_camera_count', { current: videoDeviceIdx + 1, total: videoDevices.length }) : t('broadcast.flip_camera')}
                </span>
              )}
            </Button>
          </>
        )}
        <Button
          data-testid="broadcast-end-btn"
          onClick={end}
          disabled={ending}
          variant={embedded ? 'ghost' : 'destructive'}
          size={embedded ? 'icon' : 'sm'}
          className={
            embedded
              ? `${controlsVisible ? 'pointer-events-auto' : 'pointer-events-none'} h-11 w-11 rounded-full bg-red-500/85 text-white backdrop-blur-md hover:bg-red-500 disabled:opacity-60`
              : 'ml-auto shrink-0 gap-1'
          }
          aria-label={t('broadcast.end_broadcast_label')}
        >
          {ending ? <Loader2 className="h-5 w-5 animate-spin" /> : <X className="h-5 w-5" />}
          {!embedded && <span className="hidden sm:inline">{t('broadcast.end_broadcast')}</span>}
        </Button>
      </div>
    </div>
  );
};

export default LiveKitBroadcastRoom;
